// utils/wordCounter.ts
// Word counting utility for HTML content

import { stripHtmlTags } from './htmlSanitizer';

/**
 * Counts words in HTML content
 * Strips HTML tags and counts actual words
 * @param html - HTML content string
 * @returns Word count object with detailed metrics
 */
export const countWords = (html: string): {
    words: number;
    characters: number;
    charactersNoSpaces: number;
    sentences: number;
    paragraphs: number;
} => {
    if (!html || typeof html !== 'string') {
        return {
            words: 0,
            characters: 0,
            charactersNoSpaces: 0,
            sentences: 0,
            paragraphs: 0,
        };
    }

    // Strip HTML tags to get plain text
    const plainText = stripHtmlTags(html);

    // Count words (split by whitespace and filter empty strings)
    const words = plainText
        .split(/\s+/)
        .filter(word => word.length > 0 && /\S/.test(word)).length;

    // Count characters
    const characters = plainText.length;
    const charactersNoSpaces = plainText.replace(/\s/g, '').length;

    // Count sentences (rough estimate based on punctuation)
    const sentences = (plainText.match(/[.!?]+/g) || []).length || (plainText.length > 0 ? 1 : 0);

    // Count paragraphs from HTML
    const paragraphs = (html.match(/<p>/g) || []).length || (html.length > 0 ? 1 : 0);

    return {
        words,
        characters,
        charactersNoSpaces,
        sentences,
        paragraphs,
    };
};

/**
 * Gets formatted word count string
 * @param html - HTML content
 * @returns Formatted string like "450 words"
 */
export const getWordCountString = (html: string): string => {
    const { words } = countWords(html);

    if (words === 0) return '0 words';
    if (words === 1) return '1 word';

    // Add commas for thousands
    return `${words.toLocaleString()} words`;
};

/**
 * Estimates reading time based on word count
 * @param html - HTML content
 * @param wordsPerMinute - Reading speed (default: 200 for ages 8-14)
 * @returns Reading time in minutes
 */
export const estimateReadingTime = (html: string, wordsPerMinute: number = 200): number => {
    const { words } = countWords(html);

    if (words === 0) return 0;

    const minutes = words / wordsPerMinute;

    // Round up to nearest minute, minimum 1 minute
    return Math.max(1, Math.ceil(minutes));
};

/**
 * Gets formatted reading time string
 * @param html - HTML content
 * @param wordsPerMinute - Reading speed (default: 200)
 * @returns Formatted string like "3 min read"
 */
export const getReadingTimeString = (html: string, wordsPerMinute?: number): string => {
    const minutes = estimateReadingTime(html, wordsPerMinute);

    if (minutes === 0) return 'Less than 1 min read';
    if (minutes === 1) return '1 min read';

    return `${minutes} min read`;
};

/**
 * Validates content length for articles
 * @param html - HTML content
 * @param minWords - Minimum word count (default: 50)
 * @param maxWords - Maximum word count (optional)
 * @returns Validation result with message
 */
export const validateContentLength = (
    html: string,
    minWords: number = 50,
    maxWords?: number
): {
    isValid: boolean;
    message: string;
    currentWords: number;
} => {
    const { words } = countWords(html);

    if (words === 0) {
        return {
            isValid: false,
            message: 'Article content cannot be empty',
            currentWords: words,
        };
    }

    if (words < minWords) {
        return {
            isValid: false,
            message: `Article must be at least ${minWords} words (currently ${words} words)`,
            currentWords: words,
        };
    }

    if (maxWords && words > maxWords) {
        return {
            isValid: false,
            message: `Article must be less than ${maxWords} words (currently ${words} words)`,
            currentWords: words,
        };
    }

    return {
        isValid: true,
        message: `Content length is valid (${words} words)`,
        currentWords: words,
    };
};

/**
 * Gets detailed content metrics for display
 * @param html - HTML content
 * @returns Formatted metrics object
 */
export const getContentMetrics = (html: string): {
    wordCount: string;
    readingTime: string;
    characterCount: string;
    paragraphCount: string;
} => {
    const metrics = countWords(html);

    return {
        wordCount: getWordCountString(html),
        readingTime: getReadingTimeString(html),
        characterCount: `${metrics.characters.toLocaleString()} characters`,
        paragraphCount: `${metrics.paragraphs} paragraph${metrics.paragraphs !== 1 ? 's' : ''}`,
    };
};

/**
 * Checks if content is too short for publishing
 * @param html - HTML content
 * @param minWords - Minimum words required (default: 50)
 * @returns True if content is too short
 */
export const isContentTooShort = (html: string, minWords: number = 50): boolean => {
    const { words } = countWords(html);
    return words < minWords;
};

/**
 * Checks if content is too long for publishing
 * @param html - HTML content
 * @param maxWords - Maximum words allowed
 * @returns True if content is too long
 */
export const isContentTooLong = (html: string, maxWords: number): boolean => {
    const { words } = countWords(html);
    return words > maxWords;
};

/**
 * Gets progress percentage based on word count goal
 * @param html - HTML content
 * @param goalWords - Target word count
 * @returns Percentage complete (0-100)
 */
export const getWordCountProgress = (html: string, goalWords: number): number => {
    const { words } = countWords(html);

    if (goalWords === 0) return 0;

    const percentage = (words / goalWords) * 100;

    // Cap at 100%
    return Math.min(100, Math.round(percentage));
};

// Named exports for better tree-shaking
const wordCounterUtils = {
    countWords,
    getWordCountString,
    estimateReadingTime,
    getReadingTimeString,
    validateContentLength,
    getContentMetrics,
    isContentTooShort,
    isContentTooLong,
    getWordCountProgress,
};

export default wordCounterUtils;